import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Row, 
  Col, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  message,
  Table,
  Tooltip,
  Progress,
  Statistic
} from 'antd';
import { 
  MailOutlined, 
  PlusOutlined, 
  EditOutlined,
  SendOutlined,
  EyeOutlined,
  BarChartOutlined,
  CalendarOutlined,
  UserOutlined
} from '@ant-design/icons';
import { emailMarketingService } from '../../../services/emailMarketingService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EmailCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const campaignsData = await emailMarketingService.getCampaigns();
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      message.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (values) => {
    try {
      const campaignData = {
        name: values.name,
        subject: values.subject,
        content: values.content,
        segment: values.segment,
        scheduledDate: values.scheduledDate?.toISOString(),
        type: values.type,
        status: 'draft'
      };

      await emailMarketingService.createCampaign(campaignData);
      message.success('Campaign created successfully');
      setModalVisible(false);
      form.resetFields();
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      message.error('Failed to create campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      await emailMarketingService.sendCampaign(campaignId);
      message.success('Campaign sent successfully');
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      message.error('Failed to send campaign');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      scheduled: 'blue',
      sent: 'green',
      paused: 'orange'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.subject}
          </Text>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color="blue">{type}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (_, record) => {
        const openRate = record.sentCount > 0 ? (record.openCount / record.sentCount * 100).toFixed(1) : 0;
        const clickRate = record.sentCount > 0 ? (record.clickCount / record.sentCount * 100).toFixed(1) : 0;
        
        return (
          <div>
            <div style={{ fontSize: '0.8rem' }}>
              <Text type="secondary">Sent: {record.sentCount || 0}</Text>
            </div>
            <div style={{ fontSize: '0.8rem' }}>
              <Text type="secondary">Open: {openRate}%</Text>
            </div>
            <div style={{ fontSize: '0.8rem' }}>
              <Text type="secondary">Click: {clickRate}%</Text>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview">
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditCampaign(record)}
            />
          </Tooltip>
          {record.status === 'draft' && (
            <Tooltip title="Send">
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                size="small"
                onClick={() => handleSendCampaign(record.id)}
              >
                Send
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    form.setFieldsValue({
      ...campaign,
      scheduledDate: campaign.scheduledDate ? dayjs(campaign.scheduledDate) : null
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCampaign(null);
    form.resetFields();
  };

  // Calculate campaign statistics
  const campaignStats = {
    total: campaigns.length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Email Campaigns
            </Title>
            <Text type="secondary">
              Create and manage email marketing campaigns
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Create Campaign
          </Button>
        </div>

        {/* Campaign Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Campaigns"
                value={campaignStats.total}
                prefix={<MailOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Sent"
                value={campaignStats.sent}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Draft"
                value={campaignStats.draft}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Scheduled"
                value={campaignStats.scheduled}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={campaigns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Campaign Modal */}
      <Modal
        title={editingCampaign ? "Edit Campaign" : "Create New Campaign"}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateCampaign}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Campaign Name"
                rules={[{ required: true, message: 'Please enter campaign name' }]}
              >
                <Input placeholder="Enter campaign name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Campaign Type"
                rules={[{ required: true, message: 'Please select campaign type' }]}
              >
                <Select placeholder="Select campaign type">
                  <Option value="newsletter">Newsletter</Option>
                  <Option value="promotional">Promotional</Option>
                  <Option value="service-reminder">Service Reminder</Option>
                  <Option value="seasonal">Seasonal</Option>
                  <Option value="welcome">Welcome Series</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="subject"
            label="Email Subject"
            rules={[{ required: true, message: 'Please enter email subject' }]}
          >
            <Input placeholder="Enter email subject line" />
          </Form.Item>

          <Form.Item
            name="segment"
            label="Target Segment"
            rules={[{ required: true, message: 'Please select target segment' }]}
          >
            <Select placeholder="Select target segment">
              <Option value="all-customers">All Customers</Option>
              <Option value="new-customers">New Customers</Option>
              <Option value="active-members">Active Members</Option>
              <Option value="seasonal-customers">Seasonal Customers</Option>
              <Option value="workshop-attendees">Workshop Attendees</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduledDate"
            label="Schedule Date (Optional)"
          >
            <DatePicker 
              style={{ width: '100%' }}
              showTime
              placeholder="Select send date and time"
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Email Content"
            rules={[{ required: true, message: 'Please enter email content' }]}
          >
            <TextArea
              rows={8}
              placeholder="Enter your email content here..."
            />
          </Form.Item>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailCampaignManager;
